import { ChevronRight } from 'lucide-react';
import { ReactNode, memo, useState, useCallback, JSX } from 'react';

import {
  INTERACTIVE_STATES,
  SIZE_SCALE,
  PADDING_X,
  PADDING_Y,
  GAP_SCALE,
  TYPE_SCALE,
  RADIUS_SCALE,
  COLOR_SCALE,
  FLEX_MAP,
} from '@/components/design-system/tokens';

interface ExpandableSectionProps {
  title: string;
  icon?: ReactNode;
  content: ReactNode;
  defaultExpanded?: boolean;
}

const ExpandableSection = memo(
  ({ title, icon, content, defaultExpanded = false }: ExpandableSectionProps): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const toggleExpand = useCallback(() => {
      setIsExpanded(prev => !prev);
    }, []);

    return (
      <div
        className={`border ${COLOR_SCALE['admin-border']} ${RADIUS_SCALE.lg} sm:${RADIUS_SCALE.xl} overflow-hidden ${COLOR_SCALE.surface} hover:${COLOR_SCALE['admin-border']} transition-colors`}
      >
        <button
          onClick={toggleExpand}
          className={`${SIZE_SCALE.full} ${PADDING_X['4']} ${PADDING_Y['3']} sm:${PADDING_X['4']} sm:${PADDING_Y['4']} flex items-center justify-between hover:${COLOR_SCALE['surface-muted']} active:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.duration.normal} focus:outline-none focus:${COLOR_SCALE['surface-muted']} touch-manipulation`}
          aria-expanded={isExpanded}
        >
          <div
            className={`flex items-center ${GAP_SCALE['2']} sm:${GAP_SCALE['3']} flex-1 ${SIZE_SCALE.minW0}`}
          >
            <div className={FLEX_MAP['flex-shrink-0']}>{icon}</div>
            <span
              className={`font-semibold ${COLOR_SCALE.foreground} text-left ${TYPE_SCALE.sm} leading-snug`}
            >
              {title}
            </span>
          </div>
          <ChevronRight
            className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} sm:${SIZE_SCALE['5']} sm:${SIZE_SCALE['5']} ${COLOR_SCALE['icon-muted']} transition-transform ${INTERACTIVE_STATES.duration.fast} flex-shrink-0 ${
              isExpanded ? 'rotate-90' : ''
            }`}
          />
        </button>

        {isExpanded && (
          <div
            className={`${PADDING_X['4']} ${PADDING_Y['3']} sm:${PADDING_X['4']} sm:${PADDING_Y['4']} border-t ${COLOR_SCALE['admin-border']} animate-in slide-in-from-top-2 duration-200`}
          >
            {content}
          </div>
        )}
      </div>
    );
  }
);

ExpandableSection.displayName = 'ExpandableSection';

export default ExpandableSection;
